import React from 'react';
import PropTypes from 'prop-types';
import './BeuLoader.css';

// Built-in icons for common use cases
const icons = {
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  )
};

/**
 * BeuLoader - Clean, modern loader component with contextual messaging
 * Matches the BookingLoader design with spinner, icons, and animated dots
 *
 * @param {string} message - Main title/message to display
 * @param {string} subtitle - Optional subtitle for additional context
 * @param {string} icon - Icon name: 'lock', 'shield', 'calendar', 'search', 'user', 'star'
 * @param {boolean} fullScreen - Whether to show as full screen overlay (default: true)
 * @param {boolean} showDots - Whether to show animated dots (default: true)
 * @param {string} size - Size of the spinner: 'small', 'medium', 'large' (default: 'medium')
 * @param {boolean} transparent - Whether to use transparent background with skeleton behind (default: false)
 * @param {React.ReactNode} skeleton - Optional skeleton content to show behind transparent loader
 */
const BeuLoader = ({
  message,
  subtitle,
  icon,
  fullScreen = true,
  showDots = true,
  size = 'medium',
  transparent = false,
  skeleton = null
}) => {
  const containerClass = fullScreen
    ? `beu-loader-overlay${transparent ? ' beu-loader-transparent' : ''}`
    : 'beu-loader-inline';

  const IconComponent = icon && icons[icon] ? icons[icon] : null;

  return (
    <div className={containerClass}>
      {/* Skeleton content behind the loader */}
      {transparent && skeleton && (
        <div className="beu-loader-skeleton-bg">
          {skeleton}
        </div>
      )}
      
      {/* Loader content */}
      <div className={`beu-loader-content${transparent ? ' beu-loader-content-transparent' : ''}`}>
        {IconComponent ? (
          <div className={`beu-loader-icon beu-loader-icon-${size}`}>
            {IconComponent}
          </div>
        ) : (
          <div className={`beu-loader-spinner beu-loader-spinner-${size}`} />
        )}
        
        {message && (
          <h2 className="beu-loader-title">{message}</h2>
        )}
        
        {subtitle && (
          <p className="beu-loader-subtitle">{subtitle}</p>
        )}
        
        {showDots && (
          <div className="beu-loader-dots">
            <div className="beu-loader-dot" />
            <div className="beu-loader-dot" />
            <div className="beu-loader-dot" />
          </div>
        )}
      </div>
    </div>
  );
};

BeuLoader.propTypes = {
  message: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.oneOf(['lock', 'shield', 'calendar', 'search', 'user', 'star']),
  fullScreen: PropTypes.bool,
  showDots: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  transparent: PropTypes.bool,
  skeleton: PropTypes.node
};

BeuLoader.defaultProps = {
  message: null,
  subtitle: null,
  icon: null,
  fullScreen: true,
  showDots: true,
  size: 'medium',
  transparent: false,
  skeleton: null
};

export default BeuLoader;
